import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { VisitorConsentDocumentUpsertDraft } from "../../../features/VisitorConsent/visitor-consent-documents.context";
import {
  createVisitorConsentDocument,
  deleteVisitorConsentDocument,
  fetchVisitorConsentDocuments,
  updateVisitorConsentDocument
} from "./visit-consent-documents.api";

export const visitorConsentDocumentsQueryKey = ["visitor-consent", "documents"] as const;

export function useRemoteVisitorConsentDocuments() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const listQuery = useQuery(visitorConsentDocumentsQueryKey, async () => {
    const { data } = await fetchVisitorConsentDocuments();
    return data;
  });

  const invalidateDocuments = () => queryClient.invalidateQueries(visitorConsentDocumentsQueryKey);

  const createMutation = useMutation({
    mutationFn: async (draft: VisitorConsentDocumentUpsertDraft) => {
      const { data } = await createVisitorConsentDocument(draft);
      return data;
    },
    onSuccess: async () => {
      await invalidateDocuments();
      enqueueSnackbar(t("_visitorConsentDocuments_saveSuccess"), { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar(t("_visitorConsentDocuments_saveError"), { variant: "error" });
    }
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      documentId,
      draft
    }: {
      documentId: string;
      draft: VisitorConsentDocumentUpsertDraft;
    }) => {
      const { data } = await updateVisitorConsentDocument(documentId, draft);
      return data;
    },
    onSuccess: async () => {
      await invalidateDocuments();
      enqueueSnackbar(t("_visitorConsentDocuments_saveSuccess"), { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar(t("_visitorConsentDocuments_saveError"), { variant: "error" });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (documentId: string) => {
      await deleteVisitorConsentDocument(documentId);
    },
    onSuccess: async () => {
      await invalidateDocuments();
      enqueueSnackbar(t("_visitorConsentDocuments_deleteSuccess"), { variant: "success" });
    },
    onError: () => {
      enqueueSnackbar(t("_visitorConsentDocuments_deleteError"), { variant: "error" });
    }
  });

  return {
    listQuery,
    createMutation,
    updateMutation,
    deleteMutation
  };
}
